/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    MyShowsUserSettings,
    MyShowsUserSettingsGetRequest,
    MyShowsUserSettingsPutRequest,
    MyShowsUserSettingsValue,
    OnePassAndRecordingsUserOptionsGetRequest,
    OnePassAndRecordingsUserOptionsPutRequest,
    OnePassAndRecordingsUserSettings,
    OnePassAndRecordingsUserSettingsValues,
} from 'client-code/user-settings';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IUserSettingsServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types';

export class UserSettingsServiceRepositoryImpl implements IUserSettingsServiceRepository {
    private static TAG: string = 'UserSettingsServiceRepositoryImpl';

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;
    }

    /**
     * Helper function to create a configured DefaultApi instance
     * @returns { DefaultApi } Configured DefaultApi instance
     */
    private createUserSettingsApi(): DefaultApi {
        const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;
        const configurationParameters: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
        const configuration = new Configuration(configurationParameters);
        return new DefaultApi(configuration);
    }

    /**
     * Helper function to create request parameters
     * @returns { MyShowsUserSettingsGetRequest } Request parameters for fetching user settings
     */
    private createRequestParams():
        | MyShowsUserSettingsGetRequest
        | OnePassAndRecordingsUserOptionsGetRequest {
        const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;
        return {
            applicationName: commonHeaders.ApplicationName,
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            bodyId: this.mServiceParams.bodyId,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * Function to fetch the user settings for My Shows page
     * @returns { Promise<MyShowsUserSettings> } Returns a list of my shows orders
     */
    public async fetchMyShowsUserSettings(): Promise<MyShowsUserSettings> {
        try {
            const userSettingsApi = this.createUserSettingsApi();
            const requestParams = this.createRequestParams();

            const myShowsUserSettingGetResponse =
                await userSettingsApi.myShowsUserSettingsGet(requestParams);
            return myShowsUserSettingGetResponse.data;
        } catch (error: any) {
            Log.e(
                UserSettingsServiceRepositoryImpl.TAG,
                'UserSettingsServiceRepositoryImpl::fetchMyShowsUserSettings',
                error,
            );
            throw error;
        }
    }

    /**
     * Modify the user settings for My Shows items ordering
     * @param myShowsUserSettingsValue The new settings value to update
     * @returns { Promise<void> }
     */
    public async updateMyShowsUserSettings(myShowsUserSettingsValue: MyShowsUserSettingsValue) {
        try {
            const userSettingsApi = this.createUserSettingsApi();
            const requestParams: MyShowsUserSettingsPutRequest = {
                ...this.createRequestParams(),
                myShowsUserSettingsValue,
            };

            await userSettingsApi.myShowsUserSettingsPut(requestParams);
        } catch (error: any) {
            Log.e(
                UserSettingsServiceRepositoryImpl.TAG,
                'UserSettingsServiceRepositoryImpl::updateMyShowsUserSettings',
                error,
            );
            throw error;
        }
    }

    /**
     * Function to fetch the user settings for Onepass and Recordings page
     * @returns { Promise<OnePassAndRecordingsUserSettings> } Returns a list
     */
    public async fetchOnePassAndRecordingsSettings(): Promise<OnePassAndRecordingsUserSettings> {
        try {
            const userSettingsApi = this.createUserSettingsApi();
            const requestParams = this.createRequestParams();

            const onePassAndRecordingsUserSettingGetResponse =
                await userSettingsApi.onePassAndRecordingsUserOptionsGet(requestParams);
            return onePassAndRecordingsUserSettingGetResponse.data;
        } catch (error: any) {
            Log.e(
                UserSettingsServiceRepositoryImpl.TAG,
                'UserSettingsServiceRepositoryImpl::fetchOnePassAndRecordingsSettings',
                error,
            );
            throw error;
        }
    }

    /**
     * Modify the user settings for Onepass and Recordings items ordering
     * @param onePassAndRecordingsUserSettingsValues The new settings value to update
     * @returns { Promise<void> }
     */
    public async updateOnePassAndRecordingsSettings(
        onePassAndRecordingsUserSettingsValues: OnePassAndRecordingsUserSettingsValues,
    ) {
        try {
            const userSettingsApi = this.createUserSettingsApi();
            const requestParams: OnePassAndRecordingsUserOptionsPutRequest = {
                ...this.createRequestParams(),
                onePassAndRecordingsUserSettingsValues,
            };

            await userSettingsApi.onePassAndRecordingsUserOptionsPut(requestParams);
        } catch (error: any) {
            Log.e(
                UserSettingsServiceRepositoryImpl.TAG,
                'UserSettingsServiceRepositoryImpl::updateOnePassAndRecordingsSettings',
                error,
            );
            throw error;
        }
    }
}
